import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

export default function DataChart({ data, loading }) {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="text-ocean-sky">Loading chart data...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="text-ocean-sky">No data available</div>
      </div>
    );
  }

  // Format data for the chart
  const chartData = data.map((item) => ({
    time: format(new Date(item.timestamp), 'HH:mm'),
    date: format(new Date(item.timestamp), 'MMM dd'),
    pm25: parseFloat(item.pm25),
    temp: parseFloat(item.temp),
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-ocean-dark/95 backdrop-blur-sm border border-ocean-light/30 rounded-lg p-4 shadow-xl">
          <p className="text-ocean-sky text-sm mb-2">
            {payload[0].payload.date} at {payload[0].payload.time}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
              {entry.name}: {entry.value.toFixed(2)} {entry.name === 'PM2.5' ? 'µg/m³' : '°C'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <defs>
          <linearGradient id="colorPM25" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id="colorTemp" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" opacity={0.3} />
        <XAxis
          dataKey="time"
          stroke="#7dd3fc"
          style={{ fontSize: '12px' }}
        />
        <YAxis
          yAxisId="left"
          stroke="#F59E0B"
          style={{ fontSize: '12px' }}
          label={{ value: 'PM2.5 (µg/m³)', angle: -90, position: 'insideLeft', fill: '#F59E0B' }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#3B82F6"
          style={{ fontSize: '12px' }}
          label={{ value: 'Temperature (°C)', angle: 90, position: 'insideRight', fill: '#3B82F6' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: '20px' }}
          iconType="line"
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="pm25"
          stroke="#F59E0B"
          strokeWidth={2}
          dot={{ fill: '#F59E0B', r: 3 }}
          activeDot={{ r: 5 }}
          name="PM2.5"
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="temp"
          stroke="#3B82F6"
          strokeWidth={2}
          dot={{ fill: '#3B82F6', r: 3 }}
          activeDot={{ r: 5 }}
          name="Temperature"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
